import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatContext } from '../../../../Chat.hook.ts';
import './ChatInfoNotes.styles.scss';
import Note from './components/Note.component.tsx';

export default function ChatInfoNotes(): JSX.Element {
  const { notes, setSelectedNote } = useChatContext();
  const { t } = useTranslation(['chat/chat']);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isActive || isEmpty(notes)) return;

      e.stopPropagation();

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, notes.length - 1));
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      }

      if (e.key === 'Enter' && notes.length > 0) {
        e.preventDefault();
        const selectedNote = notes[selectedIndex];
        if (!selectedNote) return;

        setSelectedNote({
          id: selectedNote.id,
          title: selectedNote.note?.title || '',
          content: selectedNote.content?.text || '',
          attachments: selectedNote.content?.attachments || undefined,
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [notes, selectedIndex, isActive]);

  // Ensure the selected note is always visible
  useEffect(() => {
    itemsRef.current[selectedIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }, [selectedIndex]);

  // Reset the index if the list of notes changes
  useEffect(() => {
    setSelectedIndex((prev) => Math.min(prev, notes.length - 1));
  }, [notes.length]);

  return (
    <>
      {isEmpty(notes) ? (
        <div className="ChatInfoNotes__Content">
          <p>{t('notes.noNotes')}</p>
        </div>
      ) : (
        <div
          className="ChatInfoNotes__Notes"
          onMouseEnter={() => setIsActive(true)}
          onMouseLeave={() => setIsActive(false)}
        >
          {notes.map((note, index) => (
            <Note
              ref={(el) => {
                if (el) itemsRef.current[index] = el;
              }}
              key={note.id}
              index={index}
              selectedIndex={selectedIndex}
              {...note}
            />
          ))}
        </div>
      )}
    </>
  );
}
